/**
 * MentionTextarea — a description field where typing [[ links another project
 *
 * Place at: src/components/overlays/MentionTextarea.jsx
 * Used by:  CreateProjectOverlay.jsx (desktop form AND mobile wizard)
 * Backend:  GET /api/mentions/search  (backend/app/mentions_api.py)
 *
 * HOW IT WORKS
 *   Type [[ then a few letters of a project name -> a list of matching
 *   projects appears. Arrow keys / Enter (or click) picks one, and the field
 *   gets the token [[p:123]]. The backend turns that token into a backlink
 *   when the project is saved, and the detail overlay shows it as a chip
 *   with the project's CURRENT name -- so renaming never breaks a link.
 *
 *   A textarea can only hold plain text, so the tokens stay visible here.
 *   The "Linked projects" row under the field shows what each token points
 *   to, with an x to remove it.
 *
 * FAILS QUIET
 *   On first mount it asks the backend once whether mentions are switched
 *   on (an empty search -- 200 means on, 404 means off). When off, this is
 *   an ordinary textarea: no hint, no dropdown. Safe to deploy before the
 *   ENABLE_PROJECT_MENTIONS setting is on in Azure.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link2, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { mentionsAPI, api } from '../../api';

const TOKEN_RE = /\[\[p:(\d{1,9})\]\]/g;
// "[[" followed by up to 40 characters that are not brackets or newlines,
// ending at the caret. "[[p:12" (someone typing a token by hand) is ignored.
const TRIGGER_RE = /\[\[([^[\]\n]{0,40})$/;
const MAX_TOKENS = 25;          // backend records at most 25 per project
const SEARCH_DELAY_MS = 200;

// One probe per page load, shared by every instance.
let enabledProbe = null;
const mentionsEnabled = () => {
  if (!enabledProbe) {
    enabledProbe = mentionsAPI.search('', 1)
      .then(() => true)
      .catch(() => false);
  }
  return enabledProbe;
};

// id -> name, shared across instances so a name is fetched at most once.
const nameCache = new Map();

const tokenIds = (text) => {
  const out = [];
  const seen = new Set();
  for (const m of (text || '').matchAll(TOKEN_RE)) {
    const id = Number(m[1]);
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};


export const MentionTextarea = ({
  value,
  onChange,              // receives the new string, not an event
  label,
  placeholder,
  rows = 4,
  size = 'sm',           // 'sm' desktop form, 'base' mobile wizard
}) => {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [trigger, setTrigger] = useState(null);     // { start, query } or null
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const [, forceNames] = useState(0);

  useEffect(() => {
    let cancelled = false;
    mentionsEnabled().then((on) => { if (!cancelled) setEnabled(on); });
    return () => { cancelled = true; };
  }, []);

  // ── Which [[query is the caret inside? ────────────────────────────────
  const readTrigger = useCallback((text, caret) => {
    if (!enabled) return null;
    const before = text.slice(0, caret);
    const m = before.match(TRIGGER_RE);
    if (!m) return null;
    if (/^p:\d*$/.test(m[1])) return null;           // hand-typed token
    return { start: caret - m[0].length, query: m[1] };
  }, [enabled]);

  const syncTrigger = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setTrigger(readTrigger(el.value, el.selectionStart ?? el.value.length));
  }, [readTrigger]);

  // ── Search, debounced ─────────────────────────────────────────────────
  const query = trigger?.query?.trim() ?? '';
  useEffect(() => {
    if (!trigger || query.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await mentionsAPI.search(query, 8);
        if (cancelled) return;
        const list = res.data.results || [];
        list.forEach((p) => nameCache.set(p.id, p.name));
        setResults(list);
        setActive(0);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [trigger, query]);

  // ── Names for tokens already in the text (edit mode, pasted text) ─────
  const ids = useMemo(() => tokenIds(value), [value]);
  useEffect(() => {
    if (!enabled) return;
    const missing = ids.filter((id) => !nameCache.has(id)).slice(0, MAX_TOKENS);
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((id) =>
      api.get(`/projects/${id}`)
        .then((res) => nameCache.set(id, (res.data.project || res.data).name))
        .catch(() => nameCache.set(id, null))       // null = not visible to you
    )).then(() => { if (!cancelled) forceNames((n) => n + 1); });
    return () => { cancelled = true; };
  }, [ids, enabled]);

  // ── Insert / remove ───────────────────────────────────────────────────
  const pick = (project) => {
    const el = ref.current;
    if (!el || !trigger) return;
    const caret = el.selectionStart ?? value.length;
    const token = `[[p:${project.id}]] `;
    const next = value.slice(0, trigger.start) + token + value.slice(caret);
    nameCache.set(project.id, project.name);
    onChange(next);
    setTrigger(null);
    setResults([]);
    const pos = trigger.start + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const removeToken = (id) => {
    const re = new RegExp(`\\[\\[p:${id}\\]\\] ?`, 'g');
    onChange(value.replace(re, ''));
  };

  const onKeyDown = (e) => {
    if (!trigger) return;
    if (e.key === 'Escape') {
      // Close the list, not the whole overlay.
      e.preventDefault();
      e.stopPropagation();
      setTrigger(null);
      return;
    }
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(results[active]);
    }
  };

  const atLimit = ids.length >= MAX_TOKENS;
  const textSize = size === 'base' ? 'text-base' : 'text-sm';

  return (
    <div className="space-y-1.5">
      {label && <label className="block text-sm font-medium text-zinc-300">{label}</label>}

      <div className="relative">
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setTrigger(readTrigger(e.target.value, e.target.selectionStart ?? e.target.value.length));
          }}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => { if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) syncTrigger(); }}
          onClick={syncTrigger}
          onBlur={() => setTimeout(() => setTrigger(null), 150)}
          placeholder={placeholder}
          rows={rows}
          className={cn(
            'w-full bg-zinc-800/50 border border-zinc-700/50 rounded-xl px-4 py-3 text-white',
            'placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50 transition-colors resize-none',
            textSize,
          )}
          aria-autocomplete={enabled ? 'list' : undefined}
          aria-expanded={Boolean(trigger)}
        />

        {trigger && (
          <div
            className="absolute left-0 right-0 top-full mt-1 z-30 rounded-xl border border-zinc-700/70 bg-zinc-900
                       shadow-2xl overflow-hidden"
            role="listbox"
          >
            {atLimit ? (
              <p className="px-3 py-2.5 text-[12px] text-amber-400">
                A project can link to {MAX_TOKENS} others at most.
              </p>
            ) : query.length < 2 ? (
              <p className="px-3 py-2.5 text-[12px] text-zinc-500">Type a project name to link it…</p>
            ) : searching && results.length === 0 ? (
              <p className="px-3 py-2.5 text-[12px] text-zinc-500">Searching…</p>
            ) : results.length === 0 ? (
              <p className="px-3 py-2.5 text-[12px] text-zinc-500">No projects match “{query}”.</p>
            ) : (
              results.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  // mousedown, not click: fires before the textarea's blur closes the list
                  onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
                    i === active ? 'bg-zinc-800' : 'hover:bg-zinc-800/60',
                  )}
                >
                  <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-zinc-800 flex items-center justify-center">
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />
                      : <span className="text-[11px] font-bold text-zinc-500">{p.name?.[0]?.toUpperCase()}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] text-zinc-200 truncate">{p.name}</p>
                    {p.author?.username && <p className="text-[11px] text-zinc-500 truncate">@{p.author.username}</p>}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {enabled && ids.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-zinc-500 flex items-center gap-1">
            <Link2 className="w-3 h-3" />
            Linked:
          </span>
          {ids.map((id) => {
            const name = nameCache.get(id);
            return (
              <span
                key={id}
                className={cn(
                  'inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] border',
                  name ? 'bg-sky-500/10 text-sky-300 border-sky-500/25' : 'bg-zinc-800 text-zinc-500 border-zinc-700',
                )}
              >
                {name === undefined ? '…' : name || 'unavailable project'}
                <button
                  type="button"
                  onClick={() => removeToken(id)}
                  aria-label={`Remove link to ${name || 'project'}`}
                  className="p-0.5 rounded-full hover:bg-white/10"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {enabled && ids.length === 0 && (
        <p className="text-[11px] text-zinc-600">
          Tip: type <span className="text-zinc-400 font-mono">[[</span> to link another project. It will show this one under “Mentioned by.”
        </p>
      )}
    </div>
  );
};

export default MentionTextarea;